import type { ModalOptions, TriggerPos } from '@/components/Select/types';
import type { DropdownPos } from '@/hooks/pure/useDropdownPos';

export const scrollToTargetAdjusted = (
  target: Element,
  container: Element,
  offset?: number,
) => {
  const headerOffset = offset || 0;
  const elementPosition =
    target.getBoundingClientRect().top - container.getBoundingClientRect().top;
  const offsetPosition = elementPosition + container.scrollTop - headerOffset;

  container.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  });
};

export const getQuadrant = (
  target: {
    width: number;
    height: number;
  },
  pos: {
    top: number;
    left: number;
  },
): 1 | 2 | 3 | 4 => {
  const center = [target.width / 2, target.height / 2];
  const { top, left } = pos;

  if (left > center[0]) {
    if (top < center[1]) return 1;
    return 4;
  }
  if (top < center[1]) return 2;
  return 3;
};

export const getDropdownPosWithTrigger = (
  trigger: HTMLElement | null,
  dropdown?: HTMLElement,
  options?: ModalOptions,
) => {
  const dropdownRect = dropdown?.getBoundingClientRect();
  const dropdownWidth = Math.max(
    dropdownRect?.width ?? 0,
    dropdown?.scrollWidth ?? 0,
  ) || 250;
  const dropdownHeight = Math.max(
    dropdownRect?.height ?? 0,
    dropdown?.scrollHeight ?? 0,
  ) || 500;

  const offsetX = options?.offset?.x || 0;
  const offsetY = options?.offset?.y || 0;
  const margin = 16; // 뷰포트 가장자리와의 여백

  const pos: DropdownPos = {
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
  };

  let modifiedHeight: number | undefined;

  if (!trigger) {
    return { pos, modifiedHeight };
  }

  const triggerRect = trigger.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // direction이 'right'인 경우: trigger 우측에 dropdown 배치
  if (options?.direction === 'right') {
    return calculateRightDirectionPosition(
      triggerRect,
      dropdownWidth,
      dropdownHeight,
      offsetX,
      offsetY,
      viewportWidth,
      viewportHeight,
      margin,
    );
  }

  // 기본 케이스: trigger 하단에 dropdown 배치
  return calculateDefaultPosition(
    triggerRect,
    dropdownWidth,
    dropdownHeight,
    offsetX,
    offsetY,
    viewportWidth,
    viewportHeight,
    margin,
  );
};

// direction이 'right'인 경우의 위치 계산
function calculateRightDirectionPosition(
  triggerRect: DOMRect,
  dropdownWidth: number,
  dropdownHeight: number,
  offsetX: number,
  offsetY: number,
  viewportWidth: number,
  viewportHeight: number,
  margin: number,
) {
  const pos: DropdownPos = {
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
  };
  let modifiedHeight: number | undefined;

  // 기본 위치: trigger 우측
  const defaultTop = triggerRect.top + offsetY;
  const defaultLeft = triggerRect.right + offsetX;

  // 우측 공간 체크
  const hasRightSpace = defaultLeft + dropdownWidth <= viewportWidth - margin;

  if (hasRightSpace) {
    // 우측에 공간이 있는 경우
    pos.top = defaultTop;
    pos.left = defaultLeft;
  } else {
    // 우측에 공간이 없는 경우: trigger 좌측에 배치
    pos.top = defaultTop;
    pos.right = viewportWidth - triggerRect.left + offsetX;
  }

  // 세로 위치 조정 (하단 공간 부족시)
  const bottomSpace = viewportHeight - defaultTop;
  if (dropdownHeight > bottomSpace - margin) {
    const topSpace = triggerRect.bottom;

    if (topSpace > bottomSpace) {
      // 상단 공간이 더 많은 경우: 위로 배치
      pos.bottom = viewportHeight - triggerRect.bottom + offsetY;
      pos.top = 'auto';

      if (dropdownHeight > topSpace - margin) {
        modifiedHeight = getAdjustedDropdownHeight(topSpace, margin);
      }
    } else {
      // 하단 공간이 더 많거나 같은 경우: 높이 조정
      modifiedHeight = getAdjustedDropdownHeight(bottomSpace, margin);
    }
  }

  return { pos, modifiedHeight };
}

// 기본 케이스의 위치 계산
function calculateDefaultPosition(
  triggerRect: DOMRect,
  dropdownWidth: number,
  dropdownHeight: number,
  offsetX: number,
  offsetY: number,
  viewportWidth: number,
  viewportHeight: number,
  margin: number,
) {
  const pos: DropdownPos = {
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
  };
  let modifiedHeight: number | undefined;

  // 기본 위치: trigger 하단
  const defaultTop = triggerRect.bottom + offsetY;
  const defaultLeft = triggerRect.left + offsetX;

  // 좌우 위치 결정
  const hasLeftSpace = defaultLeft + dropdownWidth <= viewportWidth - margin;

  if (hasLeftSpace) {
    // 좌측에서 시작해서 공간이 충분한 경우
    pos.left = defaultLeft;
  } else {
    // 우측 공간이 부족한 경우: 우측 정렬
    pos.right = viewportWidth - triggerRect.right - offsetX;

    // 그래도 공간이 부족하면 좌측을 margin만큼 띄워서 배치
    if (
      viewportWidth - triggerRect.right - offsetX + dropdownWidth >
      viewportWidth - margin
    ) {
      pos.right = 'auto';
      pos.left = margin;
    }
  }

  // 세로 위치 결정
  const bottomSpace = viewportHeight - defaultTop;
  const topSpace = triggerRect.top;

  if (dropdownHeight <= bottomSpace - margin) {
    // 하단에 충분한 공간이 있는 경우
    pos.top = defaultTop;
  } else if (dropdownHeight <= topSpace - margin) {
    // 하단 공간이 부족하지만 상단에 충분한 공간이 있는 경우
    pos.bottom = viewportHeight - triggerRect.top + offsetY;
  } else {
    // 상하 모두 공간이 부족한 경우: 더 공간이 많은 쪽을 선택하고 높이 조정
    if (topSpace > bottomSpace) {
      // 상단 공간이 더 많은 경우
      pos.bottom = viewportHeight - triggerRect.top + offsetY;
      modifiedHeight = getAdjustedDropdownHeight(topSpace, margin);
    } else {
      // 하단 공간이 더 많거나 같은 경우
      pos.top = defaultTop;
      modifiedHeight = getAdjustedDropdownHeight(bottomSpace, margin);
    }
  }

  return { pos, modifiedHeight };
}

export const getDropdownPosWithPos = (
  pos: TriggerPos,
  dropdownEl: HTMLElement,
) => {
  const dropdownRect = dropdownEl.getBoundingClientRect();
  const [dropdownWidth, dropdownHeight] = [
    dropdownRect.width,
    dropdownRect.height,
  ];
  let tempPos: DropdownPos = {
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
  };

  let modifiedHeight: number | undefined;

  const setPos = (pos: keyof DropdownPos, value: number) => {
    const posKey = pos as string;
    const oppositePos = ['top', 'bottom'].includes(posKey)
      ? pos === 'top'
        ? 'bottom'
        : 'top'
      : pos === 'left'
        ? 'right'
        : 'left';

    tempPos[pos] = value;
    tempPos[oppositePos] = 'auto';
  };

  tempPos = {
    top: pos.top || 0,
    left: pos.left || 0,
    right: 'auto',
    bottom: 'auto',
  };

  // case when there is no space on the right side of the trigger
  if ((pos.left || 0) + dropdownWidth > window.innerWidth) {
    setPos('right', window.innerWidth - (pos.left || 0));
  }

  // case when there is no space below
  if ((pos.top || 0) + dropdownHeight > window.innerHeight) {
    modifiedHeight = getAdjustedDropdownHeight(pos.top || 0, 16);
    setPos('bottom', window.innerHeight - (pos.top || 0));
  }

  return {
    pos: tempPos,
    modifiedHeight,
  };
};

const getAdjustedDropdownHeight = (space: number, margin: number) => {
  return Math.max(space - margin, 0);
};

export function escapeHTML(str: string) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
